// FinanceOverview — the current-fiscal-year levy landing for the board (FY
// is clock-derived via fyContext/currentFiscalYear — never hardcoded here).
// Large validated KPIs up top (budget / spent / encumbered / remaining — all
// tie to Oracle/Mobius via /api/finance/levy-kpis, never SUM(dash_finance_po)),
// ONE YTD line chart spanning the whole fiscal year (Budget / Actuals /
// Encumbrances / Remaining, from dash_finance_monthly), separate spend /
// encumbrance / PO-commitment breakdown panels (commitments are NEVER shown
// under the spend heading), and the recurring finance signals as cards.
//
// This page is THIS-YEAR-ONLY. Multi-year budget-vs-actual / by-FY history lives
// on the separate Multi-Year History page (/finance/history) — never here.
// Board/staff (view_finance).

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  useLevyKpis,
  useFinanceMonthly,
  useOverviewBreakdown,
  useFinanceAnalysis,
  runFinanceAnalysisNow,
  type FinanceSignal,
  type FinanceMonthlyRow,
  type EncumbranceSplit,
  type OverviewBreakdownResponse,
} from "../hooks/useFinance";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import FundBadge from "../components/finance/FundBadge";
import FreshnessStrip from "../components/finance/FreshnessStrip";
import StaleDataBanner from "../components/finance/StaleDataBanner";
import InfoTip from "../components/shared/InfoTip";
import PageIntro, { type VerdictTone } from "../components/shared/PageIntro";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import {
  fiscalCloseDeadlines,
  fyContext,
  fyContextFromAsOf,
} from "../lib/fiscal";
import PriorYearBanner from "../components/finance/PriorYearBanner";
import { useAuth } from "../hooks/useAuth";
import {
  asNum,
  usd0,
  usdC,
  TOOLTIP_PROPS,
  MUTED,
  GRID,
  COLORS,
} from "../components/finance/charts";
import {
  RemainingBalanceCard,
  FetchErrorBanner,
} from "../components/finance/ui";

// YTD chart series colors (theme ramp): budget = grey reference, actuals = blue,
// encumbrances = amber, remaining = green. Kept explicit so the legend + lines
// stay stable across renders.
const SERIES = {
  budget: { key: "total_budget", name: "Budget", color: MUTED },
  actuals: {
    key: "expenditures_cum",
    name: "Actuals (spent)",
    color: COLORS[1],
  },
  encumbrances: {
    key: "obligations_balance_floored",
    name: "Encumbrances",
    color: COLORS[3],
  },
  remaining: {
    key: "remaining",
    name: "Budget remaining (spending side)",
    color: COLORS[0],
  },
} as const;

export default function FinanceOverview() {
  // All KPIs come from the validated levy-kpis endpoint (ties to Oracle):
  //   budget_total / actual_total — SUM over dash_finance_bc_balance expenditure
  //   encumbered_total           — SUM(remaining) over open POs (remaining > 0)
  //   remaining_balance          — latest dash_finance_snapshot.funds_available
  // NEVER derived from SUM(dash_finance_po.total).
  const { data: kpis, isLoading: kpiLoading, error: kpisError } = useLevyKpis();
  const { data: monthly, isLoading: monthlyLoading } = useFinanceMonthly();
  const { data: breakdown, isLoading: breakdownLoading } =
    useOverviewBreakdown();

  // FY transition context. `fyc.currentFy` is the fiscal year we are IN (clock);
  // `fyc.dataFy` is the FY the loaded levy numbers are FOR (their as-of date).
  // When those differ (e.g. it's FY27 but the county hasn't loaded FY27 yet, so
  // we're still holding FY26), `fyc.isPriorYear` is true and we frame the page as
  // prior-year data — never as today's position. `fyLabel` labels the DATA blocks
  // with the FY they actually represent.
  // Prefer the worker's EXPLICIT fiscal_year (0094 resolved slice) over the
  // as_of derivation — the FY26 CLOSE snapshot is dated in July (pulled 7/5)
  // and would otherwise misread as current-FY data.
  const fyc = kpis?.fiscal_year
    ? fyContext(kpis.fiscal_year)
    : fyContextFromAsOf(kpis?.as_of_date ?? null);
  const fyLabel = fyc.dataFy ?? fyc.currentFy;

  const budget = asNum(kpis?.budget_total);
  const budgetReserve = asNum(kpis?.budget_reserve);
  const spent = asNum(kpis?.actual_total);
  const encumbered = asNum(kpis?.encumbered_total);
  const encCount = asNum(kpis?.encumbered_count);
  const carryEnc = asNum(kpis?.carryover_encumbered_total);
  const carryCount = asNum(kpis?.carryover_encumbered_count);
  const remaining = asNum(kpis?.remaining_balance);
  const asOf = kpis?.as_of_date ?? null;
  const consumedPct = budget > 0 ? Math.round((spent / budget) * 100) : 0;

  // Operating-vs-Capital split of the open encumbrance (for the KPI subtitle):
  // ~$592K of the ~$725K open encumbrance is CAPITAL (apparatus/vehicles), so the
  // headline isn't read as one operating number. Server-computed via the same
  // capital detector the PO register uses.
  const encSplit = breakdown?.encumbrance_split ?? null;

  // ── Single YTD line chart — the whole fiscal year in one chart ──────────────
  // Four series from dash_finance_monthly: Budget (flat reference), Actuals
  // (expenditures_cum), Encumbrances (obligations_balance FLOORED >= 0 for
  // display — it dips negative in Mar/May from budget_date bucketing; the
  // `remaining` column already absorbs that dip), and Budget remaining
  // (spending side). The Remaining line ends at the spending-side figure
  // ($158,753), which is NOT the headline Funds Available ($24,913) — the
  // footnote below the chart explains the difference honestly.
  const series = (monthly?.data ?? []) as FinanceMonthlyRow[];
  const chartEmpty = !monthlyLoading && series.length === 0;
  const lastRow = series[series.length - 1] ?? null;

  // ── Verdict banner — derived live: Funds Available + budget consumed +
  // days to fiscal close. Tone follows the Remaining Balance thresholds.
  const { daysToClose } = fiscalCloseDeadlines();
  const verdictReady = !kpiLoading && kpis != null;
  // When the loaded data is a PRIOR fiscal year, don't render a live "running
  // tight / X days to close" verdict — that year is closed and the clock's
  // days-to-close belongs to the CURRENT FY (e.g. 363 days), which is nonsensical
  // against last year's numbers. Show the final position instead, neutral tone.
  const verdictTone: VerdictTone = fyc.isPriorYear
    ? "amber"
    : remaining < 100_000
      ? "red"
      : remaining < 300_000
        ? "amber"
        : "green";
  const verdictPhrase =
    verdictTone === "red"
      ? "running tight"
      : verdictTone === "amber"
        ? "manageable but watched"
        : "in good shape";
  const daysPhrase =
    daysToClose >= 0
      ? `${daysToClose} day${daysToClose === 1 ? "" : "s"} to fiscal close`
      : "the fiscal year is closed";
  const verdictText = fyc.isPriorYear
    ? `${fyLabel} levy is closed — final position: ${usd0(remaining)} available, ${consumedPct}% of the operating budget spent. ${fyc.currentFy} levy data (fund ${fyc.currentFund}) is not yet loaded from the county.`
    : `${fyLabel} levy is ${verdictPhrase} — ${usd0(remaining)} available, ${consumedPct}% of the operating budget spent, with ${daysPhrase}.`;

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title="Finance Overview"
          subtitle={
            <>
              DCVFD levy — {fyLabel}
              {fyc.isPriorYear ? " (prior year)" : ""} · fund {fyc.fund} · PWC
              appropriation (Oracle/Mobius) ·{" "}
              {asOf ? `as of ${asOf}` : "loading…"}
              {kpis?.funds_available_note
                ? ` · ${kpis.funds_available_note}`
                : ""}
              .
            </>
          }
          headerRight={<FundBadge kind="levy" />}
        />
        {verdictReady && (
          <PageIntro tone={verdictTone}>{verdictText}</PageIntro>
        )}
        <FreshnessStrip className="mt-3" />
        <StaleDataBanner className="mt-3" />
        <PriorYearBanner ctx={fyc} asOf={asOf} className="mt-3" />
      </div>

      <FetchErrorBanner error={kpisError} what="the finance overview" />

      {/* ── Large validated KPIs (tie to Oracle) ──────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label={
            <span className="inline-flex items-center gap-1">
              {fyLabel} Operating Budget <InfoTip term="appropriation" />
            </span>
          }
          value={usd0(budget)}
          subtitle={
            budgetReserve > 0
              ? `County ${fyLabel} base appropriation (fund ${kpis?.fund ?? ""}, cc 656500). Includes ${usd0(budgetReserve)} Reserves & Contingencies (obj 58710).`
              : `County ${fyLabel} base appropriation`
          }
          accent="neutral"
          isLoading={kpiLoading}
        />
        <KPICard
          label="Expenditures"
          value={usd0(spent)}
          subtitle={
            budget > 0 ? `${consumedPct}% of budget` : "Actuals to date"
          }
          accent={
            consumedPct >= 95 ? "red" : consumedPct >= 80 ? "amber" : "green"
          }
          isLoading={kpiLoading}
        />
        <OpenEncumbrancesCard
          encumbered={encumbered}
          encCount={encCount}
          split={encSplit}
          carryEnc={carryEnc}
          carryCount={carryCount}
          isLoading={kpiLoading || breakdownLoading}
        />
        <RemainingBalanceCard
          remaining={remaining}
          asOf={asOf}
          isLoading={kpiLoading}
        />
      </div>

      {/* ── One-line pointer to the full reconciliation (lives on Levy Position).
          Overview stays at-a-glance; the three-lens waterfall that explains why
          the object report shows a bigger "available" lives on one page only. */}
      {verdictReady && (
        <p className="text-xs text-theme-muted">
          <span className="font-semibold text-theme-primary tabular-nums">
            {usd0(remaining)}
          </span>{" "}
          {fyc.isPriorYear ? `${fyLabel} closing balance` : "available"} — see{" "}
          <Link
            to="/finance/levy"
            className="font-medium text-dcvfd-accent hover:underline"
          >
            Levy Position
          </Link>{" "}
          for the three-lens reconciliation (why the by-object report shows a
          larger figure).
        </p>
      )}

      {/* ── Single YTD chart — whole fiscal year, four series ────────────────── */}
      <ChartCard
        title="FY Budget vs Actuals vs Encumbrances — Year to Date"
        subtitle={`Whole fiscal year in one view${
          monthly?.fiscal_year ? ` (${monthly.fiscal_year})` : ""
        }: Budget (flat), Actuals (cumulative spend), Encumbrances (open commitments), and Budget remaining. Monthly levy position from Mobius.`}
        empty={chartEmpty}
        minHeight={320}
      >
        {series.length > 0 && (
          <ResponsiveContainer width="100%" height={340}>
            <LineChart
              data={series}
              margin={{ top: 16, right: 16, bottom: 4, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis
                dataKey="period_label"
                tick={{ fontSize: 11, fill: MUTED }}
              />
              <YAxis
                tickFormatter={(v) => usdC(Number(v))}
                tick={{ fontSize: 11, fill: MUTED }}
                width={56}
              />
              <Tooltip
                formatter={(v, name) => [usd0(Number(v)), name]}
                labelFormatter={(l) => String(l)}
                {...TOOLTIP_PROPS}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                iconType="plainline"
              />
              {/* Budget — flat reference line (dashed, muted). */}
              <Line
                type="monotone"
                dataKey={SERIES.budget.key}
                name={SERIES.budget.name}
                stroke={SERIES.budget.color}
                strokeWidth={1.5}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
              />
              <Line
                type="monotone"
                dataKey={SERIES.actuals.key}
                name={SERIES.actuals.name}
                stroke={SERIES.actuals.color}
                strokeWidth={2.5}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey={SERIES.encumbrances.key}
                name={SERIES.encumbrances.name}
                stroke={SERIES.encumbrances.color}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey={SERIES.remaining.key}
                name={SERIES.remaining.name}
                stroke={SERIES.remaining.color}
                strokeWidth={2.5}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
        <RemainingFootnote lastRow={lastRow} headlineRemaining={remaining} />
      </ChartCard>

      {/* ── Spent / encumbered / committed panels. Committed-by-committee is its
             OWN panel (not under "what we've spent") — it is a gross PO-header
             commitment, not expenditure, and must not read as actual spend. ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SpentPanel
          breakdown={breakdown}
          isLoading={breakdownLoading}
          totalSpent={spent}
          dataFy={fyLabel}
        />
        <EncumberedPanel breakdown={breakdown} isLoading={breakdownLoading} />
        <CommittedPanel breakdown={breakdown} isLoading={breakdownLoading} />
      </div>

      {/* ── Finance signals — as cards, below the YTD graph ──────────────────── */}
      <SignalsCards />

      {/* Quick links into the detail pages (this-year + the separate history) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { to: "/finance", label: `${fyLabel} Levy Position` },
          { to: "/finance/purchase-orders", label: "Purchase Orders" },
          { to: "/finance/history", label: "Multi-Year History" },
          { to: "/finance/board-packet", label: "Board Packet" },
        ].map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="glass rounded-xl px-4 py-3 text-sm font-medium text-theme-primary hover:bg-theme-overlay transition-colors flex items-center justify-between"
          >
            {l.label} <span className="text-theme-muted">→</span>
          </Link>
        ))}
      </div>
    </PageShell>
  );
}

// ── Open Encumbrances KPI — split Operating vs Capital ────────────────────────
// The headline is the OPERATING-fund open obligation (ties Oracle's balance-
// level obligation sum; Budget − Spent − Encumbered = Remaining foots on it).
// The split follows the county's chart-of-accounts object coding (57xxx/58xxx
// = capital), computed server-side — the 55461 treatment is an open item with
// the county and is disclosed, never pre-judged (fin-cfo C-8, 7/19). Prior-fund
// carryover POs ride their own appropriation and show as a labeled add-on.
function OpenEncumbrancesCard({
  encumbered,
  encCount,
  split,
  carryEnc,
  carryCount,
  isLoading,
}: {
  encumbered: number;
  encCount: number;
  split: EncumbranceSplit | null;
  carryEnc: number;
  carryCount: number;
  isLoading: boolean;
}) {
  const subtitle = split
    ? `Operating ${usd0(split.operating)} / Capital ${usd0(split.capital)}, per county COA object coding`
    : `${encCount} open POs · funds committed`;
  const tip = split
    ? `Open obligations ${usd0(split.total)} across ${encCount} POs on the operating fund. ` +
      `Operating ${usd0(split.operating)} (${split.operating_count} POs) · Capital ${usd0(split.capital)} ` +
      `(${split.capital_count} POs) per the county's object-segment coding (57xxx/58xxx = capital). ` +
      `The operating/capital treatment of object 55461 (Vehicle & Equipment Supplies) is an open item ` +
      `with the county; ~$450K of open POs coded 55461 are apparatus-related and may reclassify to ` +
      `capital pending the county's determination. The total is unaffected by classification.` +
      (carryEnc > 0
        ? ` Plus ${usd0(carryEnc)} on ${carryCount} FY26-carryover POs (fund 2101 — their own appropriation, not this total).`
        : "")
    : `${encCount} open POs · funds committed.`;
  return (
    <div title={tip}>
      <KPICard
        label={
          <span className="inline-flex items-center gap-1">
            Open Encumbrances <InfoTip term="encumbrance" />
          </span>
        }
        value={usd0(encumbered)}
        subtitle={
          carryEnc > 0
            ? `${subtitle} · + ${usd0(carryEnc)} carryover POs (own appropriation)`
            : subtitle
        }
        accent="amber"
        isLoading={isLoading}
      />
    </div>
  );
}

// ── One-sentence footnote under the YTD chart ─────────────────────────────────
// The green line is the SPENDING-SIDE "budget remaining" (budget − actuals −
// encumbrances, expenditure-object basis) — gross, not the controlling Funds
// Available. We say so in ONE sentence and point to Levy Position for the full
// three-lens reconciliation; we DON'T re-derive a second gap story here (that
// lives in one place now — the FundsAvailableReconciler on Levy Position).
function RemainingFootnote({
  lastRow,
  headlineRemaining,
}: {
  lastRow: FinanceMonthlyRow | null;
  headlineRemaining: number;
}) {
  if (!lastRow) return null;
  const lineEnd = asNum(lastRow.remaining);
  return (
    <p className="text-xs text-theme-muted mt-3 leading-relaxed">
      The green line is gross{" "}
      <span className="font-semibold text-theme-primary">
        budget remaining (spending side)
      </span>{" "}
      and ends at{" "}
      <span className="font-semibold text-theme-primary tabular-nums">
        {usd0(lineEnd)}
      </span>
      , not the controlling Funds Available of{" "}
      <span className="font-semibold text-theme-primary tabular-nums">
        {usd0(headlineRemaining)}
      </span>{" "}
      — see{" "}
      <Link to="/finance/levy" className="text-dcvfd-accent hover:underline">
        Levy Position
      </Link>{" "}
      for the full reconciliation.
    </p>
  );
}

// ── Spent panel — validated ACTUAL spend by object/category ───────────────────
// By-object actuals only. The by-committee roll-up used to live here too, but it
// is SUM(dash_finance_po.total) — a gross PO-header COMMITMENT, not expenditure.
// Under this heading it read as actual spend while simultaneously overstating
// (PO headers are not draw-downs) and understating (non-PO spend such as the
// employee subsidy has no PO at all). It now has its own panel below.
function SpentPanel({
  breakdown,
  isLoading,
  totalSpent,
  dataFy,
}: {
  breakdown: OverviewBreakdownResponse | null;
  isLoading: boolean;
  totalSpent: number;
  /** FY the object-level actuals represent (from the levy KPI as-of date). */
  dataFy: string;
}) {
  const byObject = breakdown?.spent_by_object ?? [];
  return (
    <div className="glass rounded-2xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-theme-primary">
          What we&apos;ve spent
        </h2>
        <p className="text-xs text-theme-muted mt-0.5">
          Actual spend{totalSpent > 0 ? ` (${usd0(totalSpent)} YTD)` : ""} — by
          object (validated actuals, ties to Oracle).
        </p>
      </div>
      <BreakdownList
        heading={`By object / category (${dataFy})`}
        rows={byObject.map((r) => ({
          label: r.object_desc,
          value: asNum(r.spent),
        }))}
        isLoading={isLoading}
        emptyText="No spend recorded."
      />
    </div>
  );
}

// ── Committed panel — gross PO commitment by committee ────────────────────────
// Deliberately OUTSIDE the "what we've spent" panel: these are PO commitments,
// not expenditure, and the two must never be read as the same number or added
// together. The API row field is `committed` (renamed from `spent`, 2026-08).
//
// HEADING SCOPE: the figure is SUM(dash_finance_po.total) for the fiscal year
// with NO status predicate, so it covers POs in EVERY status, not just open
// ones. The old heading said "(open POs)" while ~$82,266 of the live FY27
// figure sat on 3 'Pending Approval' ($41,553.60) and 2 'Closed for Invoicing'
// ($40,712.51) POs. The heading now matches what is actually summed rather than
// the query being narrowed — narrowing would MOVE a board-facing figure days
// before the send, and it would also be wrong in both directions: a 'Closed for
// Invoicing' PO is a real commitment already consumed, while 'Pending Approval'
// is not yet one. Splitting commitment by status is a separate, deliberate
// change for Frank to rule on, not a label fix.
function CommittedPanel({
  breakdown,
  isLoading,
}: {
  breakdown: OverviewBreakdownResponse | null;
  isLoading: boolean;
}) {
  const byCommittee = breakdown?.spent_by_committee ?? [];
  // Label with the FY the panel actually reflects (server falls back to the
  // latest FY with POs when the clock-FY has none yet, e.g. just after 7/1).
  const committeeFy = breakdown?.spent_by_committee_fy ?? null;
  return (
    <div className="glass rounded-2xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-theme-primary">
          What we&apos;ve committed (all purchase orders)
        </h2>
        <p className="text-xs text-theme-muted mt-0.5">
          Purchase-order commitments by committee — gross PO totals across every
          PO status (open, pending approval and closed for invoicing), not just
          open POs, and not actual expenditure. Not comparable to, and never
          added to, the spend figures.
        </p>
      </div>
      <BreakdownList
        heading={`By committee (PO commitment${committeeFy ? `, ${committeeFy}` : ""})`}
        rows={byCommittee.map((r) => ({
          label: r.committee,
          value: asNum(r.committed),
          meta: `${asNum(r.po_count)} PO${asNum(r.po_count) === 1 ? "" : "s"}`,
        }))}
        isLoading={isLoading}
        emptyText={`No committee-attributed PO commitment${committeeFy ? ` for ${committeeFy}` : ""}.`}
      />
    </div>
  );
}

// ── Encumbered panel — by vendor and by object, with amounts ──────────────────
function EncumberedPanel({
  breakdown,
  isLoading,
}: {
  breakdown: OverviewBreakdownResponse | null;
  isLoading: boolean;
}) {
  const byVendor = breakdown?.encumbered_by_vendor ?? [];
  const byObject = breakdown?.encumbered_by_object ?? [];
  const split = breakdown?.encumbrance_split ?? null;
  return (
    <div className="glass rounded-2xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-theme-primary">
          What&apos;s encumbered
        </h2>
        <p className="text-xs text-theme-muted mt-0.5">
          Open commitments on POs not yet drawn down
          {split
            ? ` — ${usd0(split.total)} total: ${usd0(split.operating)} operating · ${usd0(split.capital)} capital.`
            : "."}
        </p>
      </div>
      <BreakdownList
        heading="By vendor"
        rows={byVendor.map((r) => ({
          label: r.supplier,
          value: asNum(r.encumbered),
          meta: `${asNum(r.po_count)} PO${asNum(r.po_count) === 1 ? "" : "s"}`,
          tag: r.is_capital ? "capital" : undefined,
        }))}
        isLoading={isLoading}
        emptyText="No open encumbrances."
      />
      <BreakdownList
        heading="By object"
        rows={byObject.map((r) => ({
          label: r.object_desc,
          value: asNum(r.encumbered),
          meta: `${asNum(r.po_count)} PO${asNum(r.po_count) === 1 ? "" : "s"}`,
          tag: r.is_capital ? "capital" : undefined,
        }))}
        isLoading={isLoading}
        emptyText="No open encumbrances."
      />
    </div>
  );
}

// Shared ranked breakdown list (label · amount, with optional meta + capital tag).
interface BreakdownItem {
  label: string;
  value: number;
  meta?: string;
  tag?: "capital";
}
function BreakdownList({
  heading,
  rows,
  isLoading,
  emptyText,
}: {
  heading: string;
  rows: BreakdownItem[];
  isLoading: boolean;
  emptyText: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.value)), 0);
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-theme-muted">
        {heading}
      </h3>
      {isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-theme-overlay" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-theme-muted">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => {
            const pct =
              max > 0 ? Math.max(2, (Math.abs(r.value) / max) * 100) : 0;
            return (
              <li key={`${r.label}:${i}`} className="text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-theme-primary" title={r.label}>
                    {r.label}
                    {r.tag === "capital" && (
                      <span className="ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-status-amber/15 text-status-amber">
                        capital
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums font-semibold text-theme-primary">
                    {usd0(r.value)}
                    {r.meta ? (
                      <span className="ml-1.5 font-normal text-theme-muted">
                        {r.meta}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-theme-overlay overflow-hidden">
                  <div
                    className="h-full rounded-full bg-dcvfd-accent/60"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── "What changed" signal cards — output of the scheduled finance-analysis ────
// engine. Persisted signals (budget-control projections + vendor anomalies) with
// delta state vs the prior run. Rendered as a grid of cards (one per signal).
// Refreshable on demand (admin) before a board meeting.

const SEV_BORDER: Record<string, string> = {
  critical: "border-status-red",
  warning: "border-status-amber",
  info: "border-status-green",
};
const SEV_DOT: Record<string, string> = {
  critical: "bg-status-red",
  warning: "bg-status-amber",
  info: "bg-status-green",
};
const DELTA_LABEL: Record<string, string> = {
  new: "NEW",
  worsening: "WORSE",
  improving: "BETTER",
  unchanged: "",
  resolved: "RESOLVED",
};
const DELTA_CLASS: Record<string, string> = {
  new: "bg-status-red/15 text-status-red",
  worsening: "bg-status-amber/15 text-status-amber",
  improving: "bg-status-green/15 text-status-green",
  unchanged: "bg-theme-overlay text-theme-muted",
  resolved: "bg-status-green/15 text-status-green",
};
const CATEGORY_LABEL: Record<string, string> = {
  budget_control: "Budget control",
  vendor_anomaly: "Vendor anomaly",
};

function SignalCard({ s }: { s: FinanceSignal }) {
  const deltaKey = s.delta_state ?? "unchanged";
  const delta = asNum(s.delta_since_last);
  const hasDelta = s.delta_since_last != null && Math.abs(delta) >= 1;
  return (
    <div
      className={`glass rounded-2xl p-4 border-l-4 ${SEV_BORDER[s.severity] ?? "border-theme-subtle"} flex flex-col gap-2`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-theme-muted uppercase tracking-wider">
          <span
            className={`h-2 w-2 rounded-full ${SEV_DOT[s.severity] ?? "bg-theme-muted"}`}
          />
          {CATEGORY_LABEL[s.category] ?? s.category}
        </span>
        {DELTA_LABEL[deltaKey] && (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${DELTA_CLASS[deltaKey]}`}
          >
            {DELTA_LABEL[deltaKey]}
          </span>
        )}
      </div>
      {s.subject_label && (
        <div
          className="text-sm font-semibold text-theme-primary truncate"
          title={s.subject_label}
        >
          {s.subject_label}
        </div>
      )}
      <p className="text-sm text-theme-muted leading-snug">{s.message}</p>
      {hasDelta && (
        <div className="text-xs text-theme-muted tabular-nums mt-auto pt-1">
          Δ since last run:{" "}
          <span className="font-semibold text-theme-primary">
            {delta > 0 ? "+" : ""}
            {usdC(delta)}
          </span>
        </div>
      )}
    </div>
  );
}

function SignalsCards() {
  const { data, refresh } = useFinanceAnalysis();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The manual-refresh endpoint (POST /api/finance/analysis/run) is admin-only
  // (requireRole 'admin'). This card renders for every view_finance user, so
  // hide the button for non-admins instead of surfacing a guaranteed 403 — the
  // weekly scheduled run still keeps signals fresh for board/staff.
  const canRefresh = user?.role === "admin";

  const run = data?.run ?? null;
  const signals = (data?.signals ?? []).filter(
    (s) => s.delta_state !== "resolved",
  );
  const resolved = (data?.signals ?? []).filter(
    (s) => s.delta_state === "resolved",
  );
  const critical =
    run?.summary?.critical ??
    signals.filter((s) => s.severity === "critical").length;
  const warning =
    run?.summary?.warning ??
    signals.filter((s) => s.severity === "warning").length;
  const newCount = run?.summary?.by_state?.new ?? 0;

  async function runNow() {
    setBusy(true);
    setErr(null);
    try {
      await runFinanceAnalysisNow();
      refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Refresh failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-theme-primary">
            What changed · finance signals
          </h2>
          <p className="text-xs text-theme-muted mt-0.5">
            Scheduled weekly analysis — budget-control projections + vendor
            anomalies.
            {run?.run_at
              ? ` Last run ${String(run.run_at).slice(0, 16).replace("T", " ")} UTC.`
              : " No run yet."}
          </p>
        </div>
        {canRefresh && (
          <button
            onClick={runNow}
            disabled={busy}
            className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-theme-primary glass hover:bg-theme-overlay transition-colors disabled:opacity-50"
          >
            {busy ? "Running…" : "Refresh now"}
          </button>
        )}
      </div>

      {err && <p className="text-xs text-status-red">{err}</p>}

      <div className="flex items-center gap-4 text-sm">
        <span className="text-status-red font-semibold tabular-nums">
          {critical} critical
        </span>
        <span className="text-status-amber font-semibold tabular-nums">
          {warning} warning
        </span>
        {newCount > 0 && (
          <span className="text-theme-muted tabular-nums">
            {newCount} new since last run
          </span>
        )}
      </div>

      {signals.length === 0 ? (
        <div className="glass rounded-2xl p-5">
          <p className="text-sm text-theme-muted">
            {run
              ? "No budget-control or vendor-anomaly signals this run — nothing projecting over budget."
              : "Run the analysis to generate signals."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {signals.map((s) => (
            <SignalCard key={`${s.category}:${s.subject_key}`} s={s} />
          ))}
        </div>
      )}

      {resolved.length > 0 && (
        <p className="text-xs text-status-green">
          {resolved.length} resolved since the last run:{" "}
          {resolved
            .slice(0, 3)
            .map((s) => s.subject_label)
            .join(", ")}
          {resolved.length > 3 ? "…" : ""}
        </p>
      )}
    </div>
  );
}
